import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  input,
  signal,
  viewChild,
} from '@angular/core';
import {TranslocoPipe} from '@jsverse/transloco';
import {TranslateDirectusPipe} from '@frontend/transloco';
import {CriteriaWeightPipe} from '../criteria-weight.pipe';
import {toggleCriterionId} from '../criteria-weights-overview.utils';
import {CriteriaWeightsCriterion} from '../step-4-3-criteria-weights-overview.types';
import {CriteriaWeightsTreeControlsComponent} from './criteria-weights-tree-controls/criteria-weights-tree-controls.component';
import {
  CriteriaWeightsTreeCriterionNodeView,
  CriteriaWeightsTreeDrag,
} from './criteria-weights-tree-chart.types';
import {
  CriteriaWeightsTreeLayout,
  layoutCriteriaWeightsTree,
  TREE_NODE_HEIGHT,
} from './criteria-weights-tree-layout';
import {
  CriteriaWeightsTreePoint,
  CriteriaWeightsTreeViewport,
  fitTreeViewport,
  panTreeViewport,
  TREE_VIEWPORT_PADDING,
  TREE_ZOOM_STEP,
  zoomTreeViewport,
} from './criteria-weights-tree-viewport';

@Component({
  selector: 'app-criteria-weights-tree-chart',
  imports: [
    TranslocoPipe,
    TranslateDirectusPipe,
    CriteriaWeightPipe,
    CriteriaWeightsTreeControlsComponent,
  ],
  templateUrl: './criteria-weights-tree-chart.component.html',
  styleUrl: './criteria-weights-tree-chart.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CriteriaWeightsTreeChartComponent {
  protected readonly TREE_NODE_HEIGHT = TREE_NODE_HEIGHT;

  readonly criteria = input.required<CriteriaWeightsCriterion[]>();
  readonly testId = input.required<string>();

  private readonly viewportElement = viewChild.required<ElementRef<HTMLElement>>('viewport');

  private readonly expandedCriteriaIds = signal<ReadonlySet<number>>(new Set());
  private readonly viewport = signal<CriteriaWeightsTreeViewport>({
    zoom: 1,
    panX: TREE_VIEWPORT_PADDING,
    panY: TREE_VIEWPORT_PADDING,
  });
  private readonly drag = signal<CriteriaWeightsTreeDrag | null>(null);

  protected readonly layout = computed<CriteriaWeightsTreeLayout>(() =>
    layoutCriteriaWeightsTree(this.criteria(), this.expandedCriteriaIds()),
  );

  protected readonly criterionNodes = computed<CriteriaWeightsTreeCriterionNodeView[]>(() =>
    this.layout().criteria.map((node) => ({
      ...node,
      toggleIcon: this.toggleIcon(node.expandable, node.expanded),
      ariaExpanded: node.expandable ? node.expanded : null,
    })),
  );

  protected readonly canvasTransform = computed<string>(() => {
    const {zoom, panX, panY} = this.viewport();

    return `translate(${panX}px, ${panY}px) scale(${zoom})`;
  });

  protected readonly zoomLabel = computed<string>(
    () => `${Math.round(this.viewport().zoom * 100)}%`,
  );

  protected readonly viewportCursorClass = computed<string>(() =>
    this.drag() ? 'cursor-grabbing' : 'cursor-grab',
  );

  constructor() {
    afterNextRender(() => this.resetView());
  }

  toggleCriterion(criterionId: number): void {
    this.expandedCriteriaIds.update((expandedIds) => toggleCriterionId(expandedIds, criterionId));
  }

  zoomIn(): void {
    this.zoomAtViewportCenter(TREE_ZOOM_STEP);
  }

  zoomOut(): void {
    this.zoomAtViewportCenter(1 / TREE_ZOOM_STEP);
  }

  resetView(): void {
    const availableWidth = this.viewportElement().nativeElement.clientWidth;

    this.viewport.set(fitTreeViewport(this.layout().width, availableWidth));
  }

  zoomWithWheel(event: WheelEvent): void {
    event.preventDefault();
    const bounds = this.viewportElement().nativeElement.getBoundingClientRect();
    const focus: CriteriaWeightsTreePoint = {
      x: event.clientX - bounds.left,
      y: event.clientY - bounds.top,
    };
    const factor = event.deltaY < 0 ? TREE_ZOOM_STEP : 1 / TREE_ZOOM_STEP;

    this.viewport.update((viewport) => zoomTreeViewport(viewport, focus, factor));
  }

  startPanning(event: PointerEvent): void {
    if (this.isCriterionNode(event.target)) {
      return;
    }

    this.viewportElement().nativeElement.setPointerCapture(event.pointerId);
    this.drag.set({pointerX: event.clientX, pointerY: event.clientY, viewport: this.viewport()});
  }

  pan(event: PointerEvent): void {
    const drag = this.drag();
    if (!drag) {
      return;
    }

    this.viewport.set(
      panTreeViewport(drag.viewport, event.clientX - drag.pointerX, event.clientY - drag.pointerY),
    );
  }

  stopPanning(event: PointerEvent): void {
    const element = this.viewportElement().nativeElement;
    if (element.hasPointerCapture(event.pointerId)) {
      element.releasePointerCapture(event.pointerId);
    }

    this.drag.set(null);
  }

  private zoomAtViewportCenter(factor: number): void {
    const element = this.viewportElement().nativeElement;
    const focus: CriteriaWeightsTreePoint = {
      x: element.clientWidth / 2,
      y: element.clientHeight / 2,
    };

    this.viewport.update((viewport) => zoomTreeViewport(viewport, focus, factor));
  }

  private toggleIcon(expandable: boolean, expanded: boolean): string {
    if (!expandable) {
      return '';
    }

    return expanded ? 'pi pi-chevron-left' : 'pi pi-chevron-right';
  }

  private isCriterionNode(target: EventTarget | null): boolean {
    return target instanceof Element && target.closest('[data-tree-criterion]') !== null;
  }
}
